import { useMemo, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { useServices } from '../hooks/useOffers';
import { categoryLabel, formatPrice, sizeLabel } from '../lib/format';
import { useI18n } from '../lib/i18n';
import type { Lang, ServiceDto, SizeKey } from '@shared/types';

interface Props {
  lang: Lang;
  onPick: (payload: { serviceId: string; sizeKey: SizeKey; quantity: number }) => void;
  onClose: () => void;
  busy?: boolean;
}

export default function ServicePicker({ lang, onPick, onClose, busy }: Props) {
  const { t } = useI18n();
  const { data: services = [], isLoading } = useServices();
  const [query, setQuery] = useState('');

  const groups = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const active = services
      .filter((s) => s.isActive)
      .filter((s) =>
        needle ? `${s.texts[lang].name} ${s.key}`.toLowerCase().includes(needle) : true,
      );

    const map = new Map<string, ServiceDto[]>();
    for (const s of active) {
      const list = map.get(s.category) ?? [];
      list.push(s);
      map.set(s.category, list);
    }
    return [...map.entries()];
  }, [services, query, lang]);

  return (
    <div className="fixed inset-0 z-30 flex items-start justify-center bg-dark/40 p-6 pt-16">
      <div className="card flex max-h-[80vh] w-full max-w-2xl flex-col overflow-hidden">
        <div className="flex items-center gap-3 border-b border-gray-200 px-5 py-4">
          <input
            autoFocus
            className="input"
            placeholder={t('picker.search')}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button className="btn-ghost" onClick={onClose} aria-label={t('common.close')}>
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-5">
          {isLoading ? (
            <div className="text-sm text-gray-500">{t('picker.loading')}</div>
          ) : groups.length === 0 ? (
            <div className="py-8 text-center text-sm text-gray-500">
              {t('picker.empty')}
            </div>
          ) : (
            groups.map(([category, list]) => (
              <div key={category} className="mb-6 last:mb-0">
                <div className="label">{categoryLabel(t, category)}</div>
                <div className="space-y-2">
                  {list.map((service) => (
                    <ServiceRow
                      key={service.id}
                      service={service}
                      lang={lang}
                      busy={busy}
                      onPick={onPick}
                    />
                  ))}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function ServiceRow({
  service,
  lang,
  busy,
  onPick,
}: {
  service: ServiceDto;
  lang: Lang;
  busy?: boolean;
  onPick: Props['onPick'];
}) {
  const { t } = useI18n();
  const prices = service.prices.length > 0 ? service.prices : [];

  return (
    <div className="rounded-lg border border-gray-200 p-3">
      <div className="mb-2 font-semibold text-dark">{service.texts[lang].name}</div>
      {prices.length === 0 ? (
        <button
          className="btn-secondary py-2 text-xs"
          disabled={busy}
          onClick={() => onPick({ serviceId: service.id, sizeKey: 'flat', quantity: 1 })}
        >
          <Plus size={14} /> {t('picker.addNoPrice')}
        </button>
      ) : (
        <div className="flex flex-wrap gap-2">
          {prices.map((p) => (
            <button
              key={p.id}
              disabled={busy}
              onClick={() => onPick({ serviceId: service.id, sizeKey: p.sizeKey, quantity: 1 })}
              className="inline-flex items-center gap-2 rounded-md border border-gray-200 px-3 py-2 text-xs transition-colors hover:border-teal hover:bg-teal-light disabled:opacity-50"
            >
              <Plus size={12} className="text-teal" />
              {service.hasSizeTiers && (
                <span className="font-medium">{sizeLabel(t, p.sizeKey)}</span>
              )}
              <span className="text-gray-600">
                {p.amount === 0 && (lang === 'cs' ? p.sublabelCs : p.sublabelEn)
                  ? lang === 'cs'
                    ? p.sublabelCs
                    : p.sublabelEn
                  : formatPrice(p.amount, p.isFrom, lang)}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
